// 1 Email의 링크를 타고 올때 사용
// 2 가입완료 form
// 3 페이지에 도착하면 querystring token 으로 이멜 정보 요청하기!
// 4 useLayoutEffect로 데이터 가져온 후 처리하기
import { useSearchParams } from 'react-router-dom';
import { useLayoutEffect, useState, useCallback } from 'react';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { axiosDummyInstance } from '@utils/axios/axiosInstance';
import { ISignUpEmail, LogInType } from '@models/auth/authModel';
import { emailChecker, passwordChecker } from '@utils/common/validators';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { getLocaleValueForFlag } from '@utils/common/language';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@reducers/index';
import { fetchLogin } from '@reducers/authSlice';
import StepProgressiveBar from '@components/molecule/auth/stepProgressiveBar';

const SignUpLink = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = searchParams.get('token');

  const [isPasswordHide, setIsPasswordHide] = useState(true);

  const [emailObj, setEmailObj] = useState<{ email: string; password: string }>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<ISignUpEmail>({
    defaultValues: {
      email: '',
      password: '',
      userName: '',
      corporateName: '',
      jobTitle: '',
      phoneNumber: '',
    },
  });

  // 요청 함수
  const requestEmailObj = useCallback(async (params) => {
    try {
      const { data, status, statusText } = await axiosDummyInstance.get('/api/auth/signuplink.json', { params });
      if (status === 200) {
        return data;
      } else {
        throw new Error(statusText);
      }
    } catch (error) {
      console.log('error: ', error);
      return alert(error);
    }
  }, []);

  useLayoutEffect(() => {
    if (params) {
      // 여기서 요청 처리
      requestEmailObj(params)
        .then((response) => {
          setEmailObj(response);
          setValue('email', response.email);
        })
        .catch((err) => alert(err));
    }
  }, []);

  // 전에 입력한 패스워드와 같은지 비교하는 함수
  const passwordCheckFn = useCallback(
    (value) => {
      if (emailObj) {
        return emailObj.password === value ? true : 'password is different from you wrote, before';
      }
    },
    [emailObj]
  );

  // submit handler
  const onSubmit = useCallback(
    async (formObj: ISignUpEmail) => {
      try {
        console.log('formObj: ', formObj);
        // TODO:  여기서 추가 등록 요청
        const { data, status, statusText } = await axiosDummyInstance.get('/api/commonGetResponse.json', {
          params: { email: formObj.email, password: formObj.password },
        });
        if (status === 200) {
          await dispatch(fetchLogin({ type: LogInType.aix, email: formObj.email, password: formObj.password }));
          // 해당 주소로 옮기기!!
          navigate('/welcome', { replace: true });
        } else {
          throw new Error(statusText);
        }
      } catch (error) {
        console.log('error: ', error);
      }
    },
    [navigate, dispatch, fetchLogin]
  );

  // 비번 보이기 처리
  const onChangePasswordHide = useCallback(() => {
    setIsPasswordHide((prev) => !prev);
  }, []);

  return (
    <>
      {emailObj ? (
        <div className="other-layout join">
          <div className="main-content">
            <header className="header">
              <div className="header-inner">
                <h1 className="header-logo">
                  <img src="/images/logo-axo-app.png" alt="axo" />
                </h1>

                <label className="select type-1">
                  <select>
                    <option>ENG</option>
                    <option>JP</option>
                    <option>KOR</option>
                  </select>
                </label>
              </div>
            </header>
            <div className="container">
              <div>
                <div className="manager">
                  <div>
                    <StepProgressiveBar step="step2" />
                  </div>
                  <h2 className="manager-tit">Start right now</h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-ipt">
                    <label className="ipt">
                      <input
                        type="text"
                        disabled={true}
                        placeholder="Enter your email address"
                        value={emailObj.email}
                      />
                    </label>
                    <div className="ipt pw">
                      <label>
                        <input
                          type={`${isPasswordHide ? 'password' : 'text'}`}
                          className={`${errors?.password ? 'error' : ''}`}
                          placeholder="비밀번호 8~20글자, 알파벳, 숫자 혼합"
                          {...register('password', {
                            required: 'password is required',
                            validate: {
                              passwordChecker: (value) => passwordCheckFn(value),
                            },
                          })}
                        />
                        {errors?.password && <span className="error-txt">{errors.password?.message}</span>}
                      </label>
                      <div className="pw-show-hide">
                        <input
                          type="checkbox"
                          className="ipt-check eyes"
                          id="checked-1"
                          checked={isPasswordHide}
                          onChange={onChangePasswordHide}
                        />
                        <label htmlFor="checked-1">
                          <span className="blind">감추기</span>
                        </label>
                      </div>
                    </div>
                    <label className="ipt">
                      <input
                        type="text"
                        placeholder="Company Name"
                        className={`${errors?.corporateName ? 'error' : ''}`}
                        {...register('corporateName', {
                          required: 'Company Name is required',
                        })}
                      />
                      {errors?.corporateName && <span className="error-txt">{errors.corporateName?.message}</span>}
                    </label>
                    <label className="ipt">
                      <input
                        type="text"
                        placeholder="Name"
                        className={`${errors?.userName ? 'error' : ''}`}
                        {...register('userName', {
                          required: 'Name is required',
                        })}
                      />
                      {errors?.userName && <span className="error-txt">{errors.userName?.message}</span>}
                    </label>
                    <div className="ipt">
                      <label className="ipt">
                        <input
                          type="text"
                          placeholder="Job Title"
                          className={`${errors?.jobTitle ? 'error' : ''}`}
                          {...register('jobTitle', {
                            required: 'Job Title is required',
                          })}
                        />
                        {errors?.jobTitle && <span className="error-txt">{errors.jobTitle?.message}</span>}
                      </label>
                    </div>
                    <label className="ipt">
                      <PhoneInput
                        international
                        countryCallingCodeEditable={false}
                        defaultCountry={getLocaleValueForFlag()}
                        // {...register('phoneNumber', {
                        //   required: 'Phone Number is required',
                        // })}
                        value={watch().phoneNumber}
                        //@ts-ignore
                        onChange={(event) => {
                          console.log('event: ', event);
                          if (!!event) {
                            clearErrors('phoneNumber');
                            console.log('escapre');
                          } else {
                            setError('phoneNumber', { message: 'Phone Number is required' });
                          }
                          return setValue('phoneNumber', event);
                        }}
                        className={`${errors?.phoneNumber ? 'error' : ''}`}
                      />
                      {errors?.phoneNumber && <span className="error-txt">{errors.phoneNumber?.message}</span>}
                    </label>
                    {/* <div className="select-api">
                      <label className="select">
                        <select>
                          <option>Korea</option>
                          <option>En</option>
                        </select>
                      </label>
                    </div> */}

                    <div className="form-help">
                      <input type="checkbox" className="ipt-check basic" id="checked-2" />
                      <label htmlFor="checked-2">I read and agree to the terms and conditions</label>
                    </div>
                  </div>

                  <div className="join-btn-create">
                    <button
                      type="submit"
                      className={`btn-b ${
                        watch()?.password?.trim().length > 0 &&
                        watch()?.corporateName?.trim().length > 0 &&
                        watch()?.userName?.trim().length > 0 &&
                        watch()?.phoneNumber?.trim().length > 0 &&
                        watch()?.jobTitle?.trim().length > 0
                          ? 'pri'
                          : ''
                      }`}
                    >
                      Start axo
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        'loading....'
      )}
    </>
  );
};

export default SignUpLink;
